import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { createEncaissement } from '../redux/slices/facturesSlice';
import { AppDispatch } from '../redux/store';

interface EncaissementFormProps {
  facture: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EncaissementForm: React.FC<EncaissementFormProps> = ({
  facture,
  onSuccess,
  onCancel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<{
    montant: number;
    mode_paiement: 'especes' | 'carte' | 'virement' | 'cheque' | 'mobile';
    date_encaissement: string;
    notes: string;
  }>({
    montant: facture.montant_restant,
    mode_paiement: 'especes',
    date_encaissement: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(montant);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const montant = parseFloat(formData.montant.toString());

    if (montant <= 0) {
      setError('Le montant doit être supérieur à zéro');
      setLoading(false);
      return;
    }

    if (montant > facture.montant_restant) {
      setError('Le montant ne peut pas dépasser le montant restant');
      setLoading(false);
      return;
    }

    try {
      await dispatch(createEncaissement({
        factureId: facture.id,
        data: {
          ...formData,
          montant,
        },
      })).unwrap();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement de l\'encaissement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Informations facture */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Facture N° {facture.numero_facture}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Montant total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatMontant(facture.montant_total)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Déjà payé
            </Typography>
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
              {formatMontant(facture.montant_paye)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Reste à payer
            </Typography>
            <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
              {formatMontant(facture.montant_restant)}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        {/* Montant */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            type="number"
            label="Montant à encaisser"
            name="montant"
            value={formData.montant}
            onChange={handleChange}
            inputProps={{
              min: 0.01,
              max: facture.montant_restant,
              step: 0.01,
            }}
            helperText={`Maximum: ${formatMontant(facture.montant_restant)}`}
          />
        </Grid>

        {/* Mode de paiement */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="Mode de paiement"
            name="mode_paiement"
            value={formData.mode_paiement}
            onChange={handleChange}
          >
            <MenuItem value="especes">Espèces</MenuItem>
            <MenuItem value="carte">Carte bancaire</MenuItem>
            <MenuItem value="virement">Virement</MenuItem>
            <MenuItem value="cheque">Chèque</MenuItem>
            <MenuItem value="mobile">Mobile Money</MenuItem>
          </TextField>
        </Grid>

        {/* Date encaissement */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="date"
            label="Date d'encaissement"
            name="date_encaissement"
            value={formData.date_encaissement}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes / Commentaires"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Informations complémentaires sur l'encaissement..."
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer l\'encaissement'}
        </Button>
      </Box>
    </Box>
  );
};

export default EncaissementForm;
